import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';

import HelpArticleLink from '../../components/help-article-link';
import { INSURANCE_HYPERLINK_CLICK } from '../../constants/analytics-events';
import { getShippedRecentPath } from '../../routes/constants';
import { linkIDs } from '../../services/link-service/links';
import { ModalPortal } from '../../components/modal-portal';
import { useTrackInsuranceEvent } from '../hooks/use-track-insurance-event';
import { EventSourceStrings } from '../../tracking/tracking-constants';

import { messages } from './messages';
import style from './style.module.less';

const shippedRecentPath = getShippedRecentPath();
const LINK_TYPE_CLAIM_INSTRUCTIONS = 'claimInstructions';
const LINK_TYPE_EXTERNAL_CLAIM_URL = 'externalClaimUrl';

export const InsuranceModal = ({ close, fulfillmentPlanId, insuranceProviderName, shipmentId, url }) => {
  const trackHyperlinkClick = useTrackInsuranceEvent(INSURANCE_HYPERLINK_CLICK, EventSourceStrings.INSURANCE_CLAIMS_MODAL);

  const handleClaimInstructionsClick = useCallback(() => {
    trackHyperlinkClick({ insuranceProvider: insuranceProviderName, linkType: LINK_TYPE_CLAIM_INSTRUCTIONS });
  }, [insuranceProviderName, trackHyperlinkClick]);

  const handleContinueClick = useCallback(
    (e) => {
      e.stopPropagation();
      trackHyperlinkClick({ insuranceProvider: insuranceProviderName, linkType: LINK_TYPE_EXTERNAL_CLAIM_URL });
    },
    [insuranceProviderName, trackHyperlinkClick],
  );

  return (
    <ModalPortal onClose={close}>
      <Modal className={style['order-details-modal']}>
        <ModalHeader>
          <FormattedMessage values={{ insuranceProviderName }} {...messages.claimModalHeader} />
        </ModalHeader>
        <ModalBody className={style['order-details-modal-body']}>
          <FormattedMessage values={{ shipmentId, insuranceProviderName }} {...messages.claimModalBody} />
          <ul>
            <li className={style.item}>
              <FormattedMessage
                values={{
                  shipmentId,
                  a: (chunks) => (
                    <Button
                      className={style.link}
                      href={`${shippedRecentPath}/shipment/${fulfillmentPlanId}`}
                      onClick={close}
                      target='_self'
                      theme='link'
                    >
                      {chunks}
                    </Button>
                  ),
                }}
                {...messages.claimsModalShipmentDetails}
              />
            </li>
            <li className={style.item}>
              <FormattedMessage
                values={{
                  insuranceProviderName,
                  a: (chunks) => (
                    <HelpArticleLink articleId={linkIDs[`File${insuranceProviderName}Claim`]} onClick={handleClaimInstructionsClick}>
                      {chunks}
                    </HelpArticleLink>
                  ),
                }}
                {...messages.claimsModalClaimsInsructions}
              />
            </li>
          </ul>
        </ModalBody>
        <ModalFooter className={style['insurance-modal-footer']}>
          <Button onClick={close} theme='tertiary'>
            <FormattedMessage {...messages.claimModalFooterButtonCancel} />
          </Button>
          <Button href={url} onClick={handleContinueClick} theme='primary'>
            <FormattedMessage values={{ insuranceProviderName }} {...messages.claimModalFooterButtonContinue} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

InsuranceModal.propTypes = {
  close: PropTypes.func.isRequired,
  fulfillmentPlanId: PropTypes.string.isRequired,
  insuranceProviderName: PropTypes.string.isRequired,
  shipmentId: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};
